import { lazy } from 'react';
import type React from 'react';
import type { RouteObject } from 'react-router-dom';
import { AdminPermissionGate } from '@/components/admin/AdminPermissionGate';
import { ADMIN_PAGE_PERMISSIONS } from '@/utils/adminPermissions';

// ===== 管理后台页面（懒加载） =====
const AdminNotFoundPage = lazy(() => import('@/pages/admin/AdminNotFoundPage'));
const DashboardPage = lazy(() => import('@/pages/admin/DashboardPage'));
const PackageManagementPage = lazy(() => import('@/pages/admin/PackageManagementPage'));
const PackageFormPage = lazy(() => import('@/pages/admin/PackageFormPage'));
const OrderManagementPage = lazy(() => import('@/pages/admin/OrderManagementPage'));
const PaymentManagementPage = lazy(() => import('@/pages/admin/PaymentManagementPage'));
const WalletManagementPage = lazy(() => import('@/pages/admin/WalletManagementPage'));
const ContentManagementPage = lazy(() => import('@/pages/admin/ContentManagementPage'));
const ArticleFormPage = lazy(() => import('@/pages/admin/ArticleFormPage'));
const CountryManagementPage = lazy(() => import('@/pages/admin/CountryManagementPage'));
const DestinationManagementPage = lazy(() => import('@/pages/admin/DestinationManagementPage'));
const CategoryManagementPage = lazy(() => import('@/pages/admin/CategoryManagementPage'));
const TagManagementPage = lazy(() => import('@/pages/admin/TagManagementPage'));
const FAQManagementPage = lazy(() => import('@/pages/admin/FAQManagementPage'));
const TaxManagementPage = lazy(() => import('@/pages/admin/TaxManagementPage'));
const AdminUserManagementPage = lazy(() => import('@/pages/admin/AdminUserManagementPage'));
const RoleManagementPage = lazy(() => import('@/pages/admin/RoleManagementPage'));
const AuditLogPage = lazy(() => import('@/pages/admin/AuditLogPage'));
const SystemConfigPage = lazy(() => import('@/pages/admin/SystemConfigPage'));
const SEOManagementPage = lazy(() => import('@/pages/admin/SEOManagementPage'));
const RecommendManagementPage = lazy(() => import('@/pages/admin/RecommendManagementPage'));
const TravelerTypeManagementPage = lazy(() => import('@/pages/admin/TravelerTypeManagementPage'));
const FileManagementPage = lazy(() => import('@/pages/admin/FileManagementPage'));
const MembershipTierManagementPage = lazy(() => import('@/pages/admin/MembershipTierManagementPage'));
const CustomerManagementPage = lazy(() => import('@/pages/admin/CustomerManagementPage'));
const CustomerDetailPage = lazy(() => import('@/pages/admin/CustomerDetailPage'));

const P = ADMIN_PAGE_PERMISSIONS;

/** Wrap a page so admins without `permission` see a "no access" panel. */
const gated = (permission: string, page: React.ReactNode) => (
  <AdminPermissionGate permission={permission}>{page}</AdminPermissionGate>
);

/** 管理后台路由（AdminGuard + AdminLayout 下） */
export const adminRoutes: RouteObject[] = [
  { index: true, element: gated(P.dashboard, <DashboardPage />) },
  { path: 'packages', element: gated(P.packages, <PackageManagementPage />) },
  { path: 'packages/create', element: gated(P.packages, <PackageFormPage />) },
  { path: 'packages/new', element: gated(P.packages, <PackageFormPage />) },
  { path: 'packages/:id/edit', element: gated(P.packages, <PackageFormPage />) },
  { path: 'customers', element: gated(P.customers, <CustomerManagementPage />) },
  { path: 'customers/:id', element: gated(P.customers, <CustomerDetailPage />) },
  { path: 'orders', element: gated(P.orders, <OrderManagementPage />) },
  { path: 'payments', element: gated(P.payments, <PaymentManagementPage />) },
  { path: 'wallets', element: gated(P.wallets, <WalletManagementPage />) },
  { path: 'articles', element: gated(P.content, <ContentManagementPage />) },
  { path: 'articles/new', element: gated(P.content, <ArticleFormPage />) },
  { path: 'articles/:id/edit', element: gated(P.content, <ArticleFormPage />) },
  { path: 'countries', element: gated(P.content, <CountryManagementPage />) },
  { path: 'destinations', element: gated(P.content, <DestinationManagementPage />) },
  { path: 'categories', element: gated(P.content, <CategoryManagementPage />) },
  { path: 'tags', element: gated(P.content, <TagManagementPage />) },
  { path: 'faq', element: gated(P.content, <FAQManagementPage />) },
  { path: 'tax', element: gated(P.tax, <TaxManagementPage />) },
  { path: 'admins', element: gated(P.admins, <AdminUserManagementPage />) },
  { path: 'roles', element: gated(P.roles, <RoleManagementPage />) },
  { path: 'audit-logs', element: gated(P.auditLogs, <AuditLogPage />) },
  { path: 'config', element: gated(P.config, <SystemConfigPage />) },
  { path: 'seo', element: gated(P.seo, <SEOManagementPage />) },
  { path: 'recommendations', element: gated(P.content, <RecommendManagementPage />) },
  { path: 'traveler-types', element: gated(P.content, <TravelerTypeManagementPage />) },
  { path: 'files', element: gated(P.content, <FileManagementPage />) },
  { path: 'membership-tiers', element: gated(P.membershipTiers, <MembershipTierManagementPage />) },
  { path: '*', element: <AdminNotFoundPage /> },
];
